import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Plus, Trophy, Edit, Trash2, RotateCcw, Users, Crown, Mail, Building2, Calendar, Globe, Lock, Download, List, History, Clock, UserCheck, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Logo from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [lists, setLists] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [newClub, setNewClub] = useState('');
  const [newList, setNewList] = useState({ name: '', necessary_wins: '', description: '', is_public: false, ends_at: '', club_id: null });
  const [editingList, setEditingList] = useState(null);
  const [editingUserEmail, setEditingUserEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [hideDeletedUsers, setHideDeletedUsers] = useState(true);
  const [userMatchesDialog, setUserMatchesDialog] = useState({ open: false, userId: null, userName: '', matches: [] });
  const [loggedInUsersDialog, setLoggedInUsersDialog] = useState({ open: false, users: [] });
  const [passwordResetDialog, setPasswordResetDialog] = useState({ open: false, userId: null, userName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [emailLogsDialog, setEmailLogsDialog] = useState({ open: false, logs: [] });
  const [clubMembersDialog, setClubMembersDialog] = useState({ open: false, clubName: '', clubId: '', members: [] });
  const [clubRequests, setClubRequests] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, listsRes, usersRes, clubsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/ranking-lists`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/clubs`)
      ]);
      
      setUser(userRes.data);
      if (userRes.data.role !== 'admin' && userRes.data.role !== 'club_admin') {
        toast.error('Access denied. Admin only.');
        navigate('/dashboard');
        return;
      }
      
      setLists(listsRes.data);
      setAllUsers(usersRes.data);
      setClubs(clubsRes.data);
      
      // Load club join requests
      try {
        const requestsRes = await axios.get(`${API}/admin/club-requests`, { withCredentials: true });
        setClubRequests(requestsRes.data || []);
      } catch (e) {
        // May not have permission
        setClubRequests([]);
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const payload = {
        ...newList,
        necessary_wins: parseInt(newList.necessary_wins) || 1,
        club_id: newList.club_id || null
      };
      await axios.post(`${API}/ranking-lists`, payload, { withCredentials: true });
      toast.success('Ranking list created successfully!');
      setNewList({ name: '', necessary_wins: '', description: '', is_public: false, ends_at: '', club_id: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create list');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateClub = async (e) => {
    e.preventDefault();
    if (!newClub.trim()) return;
    try {
      await axios.post(`${API}/admin/clubs`, { name: newClub }, { withCredentials: true });
      toast.success('Club created successfully!');
      setNewClub('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create club');
    }
  };

  const handleDeleteClub = async (clubId, clubName) => {
    try {
      await axios.delete(`${API}/admin/clubs/${clubId}`, { withCredentials: true });
      toast.success(`${clubName} deleted`);
      loadData();
    } catch (error) {
      toast.error('Failed to delete club');
    }
  };

  const handleEditList = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/ranking-lists/${editingList.list_id}`, {
        name: editingList.name,
        necessary_wins: parseInt(editingList.necessary_wins) || 1,
        description: editingList.description,
        is_public: editingList.is_public,
        ends_at: editingList.ends_at || null
      }, { withCredentials: true });
      toast.success('Ranking list updated!');
      setIsEditDialogOpen(false);
      setEditingList(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update list');
    }
  };

  const handleUploadClubLogo = async (clubId, file) => {
    if (!file) return;
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.put(`${API}/admin/clubs/${clubId}/logo`, {
          logo_data: reader.result
        }, { withCredentials: true });
        toast.success('Club logo uploaded!');
        loadData();
      } catch (error) {
        toast.error('Failed to upload logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleResetList = async (listId, listName) => {
    try {
      await axios.delete(`${API}/ranking-lists/${listId}/reset`, { withCredentials: true });
      toast.success(`${listName} has been reset!`);
      loadData();
    } catch (error) {
      toast.error('Failed to reset list');
    }
  };

  const handlePromoteUser = async (userId, userName) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/promote`, {}, { withCredentials: true });
      toast.success(`${userName} promoted to admin!`);
      loadData();
    } catch (error) {
      toast.error('Failed to promote user');
    }
  };

  const handleToggleAdmin = async (userId, userName, currentRole) => {
    try {
      if (currentRole === 'admin') {
        // Demote from admin
        await axios.put(`${API}/admin/users/${userId}/demote`, {}, { withCredentials: true });
        toast.success(`${userName} is now a regular user`);
      } else {
        // Promote to admin
        await axios.put(`${API}/admin/users/${userId}/promote`, {}, { withCredentials: true });
        toast.success(`${userName} promoted to admin!`);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to change user role');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(`User deleted and anonymized`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAssignClubAdmin = async (userId, userName, clubId, clubName) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/club-admin`, {
        user_id: userId,
        club_id: clubId
      }, { withCredentials: true });
      toast.success(`${userName} is now club admin of ${clubName}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign club admin');
    }
  };

  const handleRemoveClubAdmin = async (userId, userName) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}/club-admin`, { withCredentials: true });
      toast.success(`${userName} is no longer a club admin`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove club admin');
    }
  };

  const handleViewClubMembers = async (clubName, clubId) => {
    try {
      const members = allUsers.filter(u => (u.clubs || []).includes(clubName) && !u.deleted);
      setClubMembersDialog({ open: true, clubName, clubId, members });
    } catch (error) {
      toast.error('Failed to load club members');
    }
  };

  const handleKickMember = async (userId, userName) => {
    try {
      await axios.post(`${API}/admin/kick-member/${userId}`, {}, { withCredentials: true });
      toast.success(`${userName} wurde aus dem Club entfernt`);
      // Update local state
      setClubMembersDialog(prev => ({
        ...prev,
        members: prev.members.filter(m => m.user_id !== userId)
      }));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to kick member');
    }
  };

  const handleApproveClubRequest = async (requestId) => {
    try {
      await axios.post(`${API}/admin/club-requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success('Anfrage genehmigt');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Genehmigen');
    }
  };

  const handleRejectClubRequest = async (requestId) => {
    try {
      await axios.post(`${API}/admin/club-requests/${requestId}/reject`, {}, { withCredentials: true });
      toast.success('Anfrage abgelehnt');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${editingUserEmail.user_id}/email`, {
        new_email: newEmail
      }, { withCredentials: true });
      toast.success('Email updated successfully!');
      setIsEmailDialogOpen(false);
      setEditingUserEmail(null);
      setNewEmail('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change email');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${passwordResetDialog.userId}/password`, {
        new_password: newPassword
      }, { withCredentials: true });
      toast.success('Password reset successfully!');
      setPasswordResetDialog({ open: false, userId: null, userName: '' });
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleCleanupTestData = async () => {
    try {
      const response = await axios.delete(`${API}/admin/cleanup-test-data`, { withCredentials: true });
      const data = response.data;
      toast.success(`Cleanup complete! Deleted: ${data.deleted.test_users_deleted} users, ${data.deleted.test_lists_deleted} lists. Remaining: ${data.remaining.users} users, ${data.remaining.ranking_lists} lists.`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    }
  };

  const handleDeleteList = async (listId, listName) => {
    try {
      await axios.delete(`${API}/ranking-lists/${listId}`, { withCredentials: true });
      toast.success(`${listName} deleted!`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete list');
    }
  };

  const handleViewUserMatches = async (userId, userName) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/matches`, { withCredentials: true });
      setUserMatchesDialog({
        open: true,
        userId,
        userName: response.data.user_name || userName,
        matches: response.data.matches || []
      });
    } catch (error) {
      toast.error('Failed to load user matches');
    }
  };

  const handleViewLoggedInUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/logged-in-users`, { withCredentials: true });
      setLoggedInUsersDialog({
        open: true,
        users: response.data || []
      });
    } catch (error) {
      toast.error('Failed to load logged-in users');
    }
  };

  const handleViewEmailLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-logs`, { withCredentials: true });
      setEmailLogsDialog({
        open: true,
        logs: response.data || []
      });
    } catch (error) {
      toast.error('Failed to load email logs');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadReport = async (listId, listName) => {
    try {
      const response = await axios.get(`${API}/admin/ranking-lists/${listId}/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const listNameSafe = listName.replace(/\s+/g, '_').toLowerCase();
      link.setAttribute('download', `${listNameSafe}_matches_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('reportDownloaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download report');
    }
  };

  // Filter users based on hideDeletedUsers checkbox
  const filteredUsers = hideDeletedUsers 
    ? allUsers.filter(u => !u.deleted && !u.name?.startsWith('Deleted User'))
    : allUsers;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToDashboard')}
          </Button>
          <Logo size="lg" />
        </div>

        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Shield className="w-10 h-10 text-[#FF3B30]" />
              <div>
                <h1 className="text-3xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {t('adminDashboard')}
                </h1>
                <p className="text-gray-400">{t('manageSettings')}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {user?.email === 'bb@e-matrix.at' && (
                <>
                  <Button 
                    onClick={() => navigate('/admin/database')}
                    className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    {t('databaseViewer')}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        className="bg-orange-600 hover:bg-orange-700 text-white rounded-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clean Test Data
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Clean Up Test Data?</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          This will permanently delete all test users (emails containing "test", "@example.com", "admin.") and all ranking lists starting with "TEST". Your account will not be affected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleCleanupTestData}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          Yes, Clean Up
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
              <Button 
                data-testid="logged-in-users-btn"
                onClick={handleViewLoggedInUsers}
                className="bg-green-600 hover:bg-green-700 text-white rounded-full"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                {t('loggedInUsers')}
              </Button>
              <Button 
                data-testid="email-logs-btn"
                onClick={handleViewEmailLogs}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('emailLogs')}
              </Button>
              <Button 
                data-testid="all-matches-btn"
                onClick={() => navigate('/admin/all-matches')}
                className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full"
              >
                <List className="w-4 h-4 mr-2" />
                {t('allMatches')}
              </Button>
            </div>
          </div>
        </div>

        {/* Club Join Requests */}
        {clubRequests.length > 0 && (
        <Card className="bg-[#111111] border-[#27272A] mb-8 border-[#FF9500]/50">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#FF9500]" />
              {t('clubJoinRequests')}
              <span className="ml-2 bg-[#FF9500] text-white text-xs px-2 py-1 rounded-full">
                {clubRequests.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {clubRequests.map(req => (
                <div 
                  key={req.request_id}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-bold">{req.user_name}</p>
                    <p className="text-sm text-gray-400">{req.user_email}</p>
                    <p className="text-xs text-[#FF9500] mt-1">
                      {t('requestToJoin')}: {req.club_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleApproveClubRequest(req.request_id)}
                      className="bg-[#34C759] hover:bg-[#2DB550] text-white rounded-full"
                      size="sm"
                    >
                      {t('approve')}
                    </Button>
                    <Button
                      onClick={() => handleRejectClubRequest(req.request_id)}
                      variant="outline"
                      className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                      size="sm"
                    >
                      {t('reject')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}

        {/* User Management */}
        <Card className="bg-[#111111] border-[#27272A] mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t('userManagement')}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="hide-deleted"
                  checked={hideDeletedUsers}
                  onCheckedChange={(checked) => setHideDeletedUsers(checked)}
                />
                <Label htmlFor="hide-deleted" className="text-sm text-gray-400 cursor-pointer">
                  {t('hideDeletedUsers')}
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noUsers')}</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredUsers.map(u => {
                  // Get all clubs for this user
                  const userClubs = u.clubs || [];
                  
                  // Get clubs this user admins (always array now)
                  const clubAdminOf = Array.isArray(u.club_admin_of) ? u.club_admin_of : (u.club_admin_of ? [u.club_admin_of] : []);
                  
                  // Determine badge color based on role
                  const getBadgeColor = (clubName) => {
                    const club = clubs.find(c => c.name === clubName);
                    if (!club) return 'bg-[#007AFF]/20 text-[#007AFF]'; // blue for unknown
                    
                    if (u.role === 'admin') {
                      return 'bg-[#FF3B30]/20 text-[#FF3B30]'; // red for admin
                    } else if (clubAdminOf.includes(club.club_id)) {
                      return 'bg-[#34C759]/20 text-[#34C759]'; // green for club admin of this club
                    } else {
                      return 'bg-[#007AFF]/20 text-[#007AFF]'; // blue for regular member
                    }
                  };
                  
                  return (
                  <div 
                    key={u.user_id}
                    className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="flex-1">
                      <p className="text-white font-bold">{u.name}</p>
                      <p className="text-sm text-gray-400">{u.email}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {/* Show all clubs with color-coded badges */}
                        {userClubs.length > 0 ? (
                          userClubs.map(clubName => (
                            <span key={clubName} className={`inline-flex items-center gap-1 text-xs ${getBadgeColor(clubName)} px-2 py-1 rounded-full`}>
                              <Building2 className="w-3 h-3" />
                              {clubName}
                            </span>
                          ))
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-[#27272A] text-gray-500 px-2 py-1 rounded-full">
                            <Building2 className="w-3 h-3" />
                            {t('noClubOption')}
                          </span>
                        )}
                        {/* Role badges */}
                        {u.role === 'admin' && (
                          <span className="inline-flex items-center gap-1 text-xs bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-1 rounded-full">
                            <Crown className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                        {u.created_at && (
                          <span className="inline-flex items-center gap-1 text-xs bg-[#27272A] text-gray-400 px-2 py-1 rounded-full">
                            <Calendar className="w-3 h-3" />
                            {t('createdAt')}: {formatDate(u.created_at)}
                          </span>
                        )}
                        {u.last_login && (
                          <span className="inline-flex items-center gap-1 text-xs bg-[#27272A] text-gray-400 px-2 py-1 rounded-full">
                            <Clock className="w-3 h-3" />
                            {t('lastLogin')}: {formatDate(u.last_login)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button 
                        data-testid="view-user-matches-btn"
                        onClick={() => handleViewUserMatches(u.user_id, u.name)}
                        variant="outline"
                        className="border-[#27272A] hover:bg-white/10 text-white rounded-full"
                      >
                        <History className="w-4 h-4 mr-2" />
                        {t('matches')}
                      </Button>
                      
                      {/* Admin-only: Change Email */}
                      {user?.role === 'admin' && (
                      <Dialog open={isEmailDialogOpen && editingUserEmail?.user_id === u.user_id} onOpenChange={(open) => {
                        setIsEmailDialogOpen(open);
                        if (!open) {
                          setEditingUserEmail(null);
                          setNewEmail('');
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button 
                            data-testid="change-email-btn"
                            onClick={() => {
                              setEditingUserEmail(u);
                              setNewEmail(u.email);
                            }}
                            variant="outline"
                            className="border-[#27272A] hover:bg-white/10 text-white rounded-full"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Email
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#111111] border-[#27272A]">
                          <DialogHeader>
                            <DialogTitle className="text-white">Change User Email</DialogTitle>
                            <DialogDescription className="text-gray-400">
                              Update email address for {u.name}
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleChangeEmail} className="space-y-4">
                            <div>
                              <Label htmlFor="new-email" className="text-white">New Email Address</Label>
                              <Input
                                id="new-email"
                                data-testid="new-email-input"
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                className="bg-[#0A0A0A] border-[#27272A] text-white"
                                required
                              />
                            </div>
                            <Button 
                              type="submit"
                              data-testid="save-email-btn"
                              className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full"
                            >
                              Update Email
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                      )}

                      {/* Admin-only: Password Reset Dialog */}
                      {user?.role === 'admin' && (
                      <Dialog open={passwordResetDialog.open && passwordResetDialog.userId === u.user_id} onOpenChange={(open) => {
                        if (!open) {
                          setPasswordResetDialog({ open: false, userId: null, userName: '' });
                          setNewPassword('');
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button 
                            data-testid="reset-password-btn"
                            onClick={() => {
                              setPasswordResetDialog({ open: true, userId: u.user_id, userName: u.name });
                            }}
                            variant="outline"
                            className="border-[#FF9500] hover:bg-[#FF9500]/10 text-[#FF9500] rounded-full"
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            Password
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#111111] border-[#27272A]">
                          <DialogHeader>
                            <DialogTitle className="text-white">Reset Password</DialogTitle>
                            <DialogDescription className="text-gray-400">
                              Set a new password for {u.name}
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                              <Label htmlFor="new-password" className="text-white">New Password</Label>
                              <Input
                                id="new-password"
                                data-testid="new-password-input"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="bg-[#0A0A0A] border-[#27272A] text-white"
                                placeholder="Minimum 6 characters"
                                minLength={6}
                                required
                              />
                            </div>
                            <Button 
                              type="submit"
                              data-testid="save-password-btn"
                              className="w-full bg-[#FF9500] hover:bg-[#E08600] text-white rounded-full"
                            >
                              Reset Password
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                      )}

                      {/* Admin-only: Admin Toggle - disabled for current user */}
                      {user?.role === 'admin' && (
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`admin-toggle-${u.user_id}`} className="text-sm text-gray-400">
                          Admin
                        </Label>
                        <Switch
                          id={`admin-toggle-${u.user_id}`}
                          data-testid="admin-toggle"
                          checked={u.role === 'admin'}
                          disabled={u.user_id === user?.user_id}
                          onCheckedChange={() => handleToggleAdmin(u.user_id, u.name, u.role)}
                          className="data-[state=checked]:bg-[#FF3B30]"
                        />
                      </div>
                      )}

                      {/* Admin-only: Club Admin Assignment */}
                      {user?.role === 'admin' && u.user_id !== user?.user_id && u.role !== 'admin' && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              data-testid="club-admin-btn"
                              variant="outline"
                              className={`rounded-full ${u.role === 'club_admin' ? 'border-[#34C759] text-[#34C759]' : 'border-[#27272A] text-white'} hover:bg-white/10`}
                            >
                              <Building2 className="w-4 h-4 mr-2" />
                              {u.role === 'club_admin' ? t('clubAdmin') : t('makeClubAdmin')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-[#111111] border-[#27272A]">
                            <DialogHeader>
                              <DialogTitle className="text-white">{t('assignClubAdmin')}</DialogTitle>
                              <DialogDescription className="text-gray-400">
                                {u.role === 'club_admin' 
                                  ? t('currentlyClubAdminOf', { club: clubs.filter(c => (Array.isArray(u.club_admin_of) ? u.club_admin_of : [u.club_admin_of]).includes(c.club_id)).map(c => c.name).join(', ') || 'Unknown' })
                                  : t('selectClubForAdmin', { name: u.name })}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 max-h-60 overflow-y-auto">
                              {clubs.map(club => (
                                <div 
                                  key={club.club_id}
                                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${(Array.isArray(u.club_admin_of) ? u.club_admin_of : []).includes(club.club_id) ? 'border-[#34C759] bg-[#34C759]/10' : 'border-[#27272A] hover:border-[#007AFF]'}`}
                                  onClick={() => handleAssignClubAdmin(u.user_id, u.name, club.club_id, club.name)}
                                >
                                  <div className="flex items-center gap-3">
                                    {club.logo_url ? (
                                      <img src={club.logo_url} alt={club.name} className="w-8 h-8 object-contain rounded" />
                                    ) : (
                                      <Building2 className="w-8 h-8 text-gray-500" />
                                    )}
                                    <span className="text-white font-medium">{club.name}</span>
                                  </div>
                                  {(Array.isArray(u.club_admin_of) ? u.club_admin_of : []).includes(club.club_id) && (
                                    <span className="text-xs bg-[#34C759]/20 text-[#34C759] px-2 py-1 rounded-full">{t('assigned')}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                            {u.role === 'club_admin' && (
                              <Button 
                                onClick={() => handleRemoveClubAdmin(u.user_id, u.name)}
                                variant="outline"
                                className="w-full mt-4 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                              >
                                {t('removeClubAdmin')}
                              </Button>
                            )}
                          </DialogContent>
                        </Dialog>
                      )}

                      {/* Admin-only: Delete User - disabled for current user */}
                      {u.user_id !== user?.user_id && user?.role === 'admin' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              data-testid="delete-user-btn"
                              variant="outline"
                              className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">{t('deleteUser')}</AlertDialogTitle>
                              <AlertDialogDescription className="text-gray-400">
                                {t('deleteUserWarning')}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">{t('cancel')}</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteUser(u.user_id, u.name)}
                                className="bg-[#FF3B30] hover:bg-[#D6301E]"
                              >
                                {t('deleteUserBtn')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        )}
                        
                        {/* Club Admin: Kick Member */}
                        {user?.role === 'club_admin' && u.user_id !== user?.user_id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="outline"
                              className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              {t('kickMember')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">{t('kickMemberTitle')}</AlertDialogTitle>
                              <AlertDialogDescription className="text-gray-400">
                                {t('kickMemberWarning', { name: u.name, club: (u.clubs || [])[0] || '' })}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">{t('cancel')}</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleKickMember(u.user_id, u.name)}
                                className="bg-[#FF3B30] hover:bg-[#D6301E]"
                              >
                                {t('kickMember')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Club Management - Admin only */}
        {user?.role === 'admin' && (
        <Card className="bg-[#111111] border-[#27272A] mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {t('clubManagement')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateClub} className="flex gap-2 mb-4">
              <Input
                data-testid="new-club-input"
                type="text"
                placeholder={t('newClubName')}
                value={newClub}
                onChange={(e) => setNewClub(e.target.value)}
                className="bg-[#0A0A0A] border-[#27272A] text-white flex-1"
              />
              <Button 
                type="submit"
                data-testid="add-club-btn"
                className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('addClub')}
              </Button>
            </form>
            {clubs.length === 0 ? (
              <p className="text-gray-400 text-center py-4">{t('noClubsYet')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {clubs.map(club => (
                  <div 
                    key={club.club_id}
                    className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 flex items-center gap-3"
                  >
                    {club.logo_url ? (
                      <img src={club.logo_url} alt={club.name} className="w-10 h-10 object-contain rounded" />
                    ) : (
                      <div className="w-10 h-10 bg-[#27272A] rounded flex items-center justify-center text-gray-500 text-xs">N/A</div>
                    )}
                    <span className="text-white flex-1">{club.name}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewClubMembers(club.name, club.club_id)}
                      className="border-[#27272A] text-white hover:bg-white/10 rounded-full"
                    >
                      <Users className="w-4 h-4 mr-1" />
                      {allUsers.filter(u => (u.clubs || []).includes(club.name) && !u.deleted).length}
                    </Button>
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden"
                        onChange={(e) => handleUploadClubLogo(club.club_id, e.target.files[0])}
                      />
                      <span className="text-xs text-[#007AFF] hover:underline">{t('uploadLogo')}</span>
                    </label>
                    <Button 
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClub(club.club_id, club.name)}
                      className="h-6 w-6 hover:bg-[#FF3B30]/20 text-[#FF3B30]"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Create New Ranking List - Admin only */}
        {user?.role === 'admin' && (
        <Card className="bg-[#111111] border-[#27272A] mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5" />
              {t('createNewList')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateList} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-white">{t('rankingListName')}</Label>
                <Input
                  id="name"
                  data-testid="list-name-input"
                  type="text"
                  placeholder="Example Ranking List"
                  value={newList.name}
                  onChange={(e) => setNewList({...newList, name: e.target.value})}
                  required
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                />
              </div>
              <div>
                <Label htmlFor="necessary_wins" className="text-white">{t('necessaryWins')}</Label>
                <Input
                  id="necessary_wins"
                  data-testid="necessary-wins-input"
                  type="number"
                  min="1"
                  placeholder="3"
                  value={newList.necessary_wins}
                  onChange={(e) => setNewList({...newList, necessary_wins: e.target.value})}
                  required
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                />
                <p className="text-xs text-gray-500 mt-1">{t('necessaryWinsHint')}</p>
              </div>
              <div>
                <Label htmlFor="description" className="text-white">{t('descriptionOptional')}</Label>
                <textarea
                  id="description"
                  data-testid="list-description-input"
                  placeholder="League rules, schedule, or other information..."
                  value={newList.description}
                  onChange={(e) => setNewList({...newList, description: e.target.value})}
                  rows={3}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                />
              </div>
              <div>
                <Label htmlFor="ends_at" className="text-white flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t('runningUntil')}
                </Label>
                <Input
                  id="ends_at"
                  data-testid="list-ends-at-input"
                  type="date"
                  value={newList.ends_at}
                  onChange={(e) => setNewList({...newList, ends_at: e.target.value})}
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                />
                <p className="text-xs text-gray-500 mt-1">{t('runningUntilHint')}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_public"
                  data-testid="list-public-checkbox"
                  checked={newList.is_public}
                  onCheckedChange={(checked) => setNewList({...newList, is_public: checked})}
                />
                <Label htmlFor="is_public" className="text-white flex items-center gap-2 cursor-pointer">
                  <Globe className="w-4 h-4 text-[#007AFF]" />
                  {t('publicList')}
                </Label>
              </div>
              <div>
                <Label htmlFor="club_id" className="text-white flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {t('clubRanking')} ({t('optional')})
                </Label>
                <select
                  id="club_id"
                  data-testid="list-club-select"
                  value={newList.club_id || ''}
                  onChange={(e) => setNewList({...newList, club_id: e.target.value || null})}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#007AFF] mt-1"
                >
                  <option value="">{t('noClubRestriction')}</option>
                  {clubs.map(club => (
                    <option key={club.club_id} value={club.club_id}>{club.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">{t('clubRankingHint')}</p>
              </div>
              <Button 
                type="submit"
                data-testid="create-list-btn"
                disabled={isCreating}
                className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full font-bold"
              >
                {isCreating ? t('loading') : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('createRankingList')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        )}

        {/* Existing Lists - Admin only */}
        {user?.role === 'admin' && (
        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              {t('manageRankingLists')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lists.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noListsCreated')}</p>
            ) : (
              <div className="space-y-3">
                {lists.map(list => (
                  <div 
                    key={list.list_id}
                    className={`bg-[#0A0A0A] border rounded-lg p-4 ${list.is_locked ? 'border-[#FF3B30]/50' : 'border-[#27272A]'}`}
                    data-testid="admin-list-card"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-bold text-lg">{list.name}</p>
                          {list.is_locked && (
                            <span className="flex items-center gap-1 text-xs bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-1 rounded-full">
                              <Lock className="w-3 h-3" />
                              {t('locked')}
                            </span>
                          )}
                          {list.is_public && (
                            <span className="flex items-center gap-1 text-xs bg-[#007AFF]/20 text-[#007AFF] px-2 py-1 rounded-full">
                              <Globe className="w-3 h-3" />
                              {t('public')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400">{t('necessaryWins')}: {list.necessary_wins || list.match_format}</p>
                        {list.created_at && (
                          <p className="text-xs text-gray-500">
                            {t('createdAt')}: {formatDate(list.created_at)}
                          </p>
                        )}
                        {list.ends_at && (
                          <p className="text-xs text-gray-500">
                            {t('until')}: {new Date(list.ends_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        onClick={() => navigate(`/ranking-lists/${list.list_id}`)}
                        className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full flex-1"
                      >
                        {t('view')}
                      </Button>
                      
                      <Button 
                        data-testid="download-report-btn"
                        onClick={() => handleDownloadReport(list.list_id, list.name)}
                        variant="outline"
                        className="border-[#27272A] hover:bg-white/10 text-white rounded-full"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {t('downloadReport')}
                      </Button>
                      
                      <Dialog open={isEditDialogOpen && editingList?.list_id === list.list_id} onOpenChange={(open) => {
                        setIsEditDialogOpen(open);
                        if (!open) setEditingList(null);
                      }}>
                        <DialogTrigger asChild>
                          <Button 
                            data-testid="edit-list-btn"
                            onClick={() => setEditingList(list)}
                            variant="outline"
                            className="border-[#27272A] hover:bg-white/10 text-white rounded-full"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            {t('edit')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#111111] border-[#27272A]">
                          <DialogHeader>
                            <DialogTitle className="text-white">{t('editRankingList')}</DialogTitle>
                            <DialogDescription className="text-gray-400">
                              {t('updateNameFormat')}
                            </DialogDescription>
                          </DialogHeader>
                          {editingList && (
                            <form onSubmit={handleEditList} className="space-y-4">
                              <div>
                                <Label htmlFor="edit-name" className="text-white">{t('name')}</Label>
                                <Input
                                  id="edit-name"
                                  data-testid="edit-list-name-input"
                                  value={editingList.name}
                                  onChange={(e) => setEditingList({...editingList, name: e.target.value})}
                                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                                />
                              </div>
                              <div>
                                <Label htmlFor="edit-necessary-wins" className="text-white">{t('necessaryWins')}</Label>
                                <Input
                                  id="edit-necessary-wins"
                                  data-testid="edit-list-necessary-wins-input"
                                  type="number"
                                  min="1"
                                  value={editingList.necessary_wins || editingList.match_format || ''}
                                  onChange={(e) => setEditingList({...editingList, necessary_wins: e.target.value})}
                                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                                />
                              </div>
                              <div>
                                <Label htmlFor="edit-description" className="text-white">{t('descriptionOptional')}</Label>
                                <textarea
                                  id="edit-description"
                                  data-testid="edit-list-description-input"
                                  placeholder="League rules, schedule, or other information..."
                                  value={editingList.description || ''}
                                  onChange={(e) => setEditingList({...editingList, description: e.target.value})}
                                  rows={3}
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                                />
                              </div>
                              <div>
                                <Label htmlFor="edit-ends-at" className="text-white flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  {t('runningUntil')}
                                </Label>
                                <Input
                                  id="edit-ends-at"
                                  data-testid="edit-list-ends-at-input"
                                  type="date"
                                  value={editingList.ends_at?.split('T')[0] || ''}
                                  onChange={(e) => setEditingList({...editingList, ends_at: e.target.value})}
                                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="edit-is-public"
                                  data-testid="edit-list-public-checkbox"
                                  checked={editingList.is_public || false}
                                  onCheckedChange={(checked) => setEditingList({...editingList, is_public: checked})}
                                />
                                <Label htmlFor="edit-is-public" className="text-white flex items-center gap-2 cursor-pointer">
                                  <Globe className="w-4 h-4 text-[#007AFF]" />
                                  {t('publicList')}
                                </Label>
                              </div>
                              <Button 
                                type="submit"
                                data-testid="save-list-edit-btn"
                                className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full"
                              >
                                {t('save')}
                              </Button>
                            </form>
                          )}
                        </DialogContent>
                      </Dialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            data-testid="reset-list-btn"
                            variant="outline"
                            className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            {t('reset')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">{t('resetRankingList')}</AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-400">
                              {t('resetWarning')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">{t('cancel')}</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleResetList(list.list_id, list.name)}
                              className="bg-[#FF3B30] hover:bg-[#D6301E]"
                            >
                              {t('resetList')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            data-testid="delete-list-btn"
                            variant="outline"
                            className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('delete')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">{t('deleteRankingList')}</AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-400">
                              {t('deleteListWarning')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">{t('cancel')}</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDeleteList(list.list_id, list.name)}
                              className="bg-[#FF3B30] hover:bg-[#D6301E]"
                            >
                              {t('deleteList')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* User Matches Dialog */}
      <Dialog open={userMatchesDialog.open} onOpenChange={(open) => {
        if (!open) setUserMatchesDialog({ open: false, userId: null, userName: '', matches: [] });
      }}>
        <DialogContent className="bg-[#111111] border-[#27272A] max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5" />
              {t('matchesFor')} {userMatchesDialog.userName}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {userMatchesDialog.matches.length} {t('matches')}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] space-y-3">
            {userMatchesDialog.matches.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noMatchesYet')}</p>
            ) : (
              userMatchesDialog.matches.map((match) => (
                <div 
                  key={match.match_id}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-[#007AFF]/20 text-[#007AFF] px-2 py-1 rounded-full">
                      {match.ranking_list_name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className={`font-bold ${match.winner_id === match.player1_id ? 'text-green-400' : 'text-white'}`}>
                        {match.player1_nickname || match.player1_name}
                      </span>
                      <div className="flex items-center gap-2 bg-[#27272A] px-3 py-1 rounded">
                        <span className={`font-bold ${match.winner_id === match.player1_id ? 'text-green-400' : 'text-white'}`}>
                          {match.player1_score}
                        </span>
                        <span className="text-gray-500">:</span>
                        <span className={`font-bold ${match.winner_id === match.player2_id ? 'text-green-400' : 'text-white'}`}>
                          {match.player2_score}
                        </span>
                      </div>
                      <span className={`font-bold ${match.winner_id === match.player2_id ? 'text-green-400' : 'text-white'}`}>
                        {match.player2_nickname || match.player2_name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 text-right">
                      <div>{t('confirmedAt')}: {formatDate(match.confirmed_at)}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Logged In Users Dialog */}
      <Dialog open={loggedInUsersDialog.open} onOpenChange={(open) => {
        if (!open) setLoggedInUsersDialog({ open: false, users: [] });
      }}>
        <DialogContent className="bg-[#111111] border-[#27272A] max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-500" />
              {t('loggedInUsers')}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {loggedInUsersDialog.users.length} {t('activeUsers')}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] space-y-3">
            {loggedInUsersDialog.users.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noActiveUsers')}</p>
            ) : (
              loggedInUsersDialog.users.map((user) => (
                <div 
                  key={user.user_id}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-bold flex items-center gap-2">
                      {user.name}
                      {user.role === 'admin' && (
                        <span className="text-xs bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded-full">
                          Admin
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-400">{user.email}</p>
                  </div>
                  <div className="text-xs text-gray-400 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {t('lastLogin')}: {formatDate(user.last_login)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Logs Dialog */}
      <Dialog open={emailLogsDialog.open} onOpenChange={(open) => {
        if (!open) setEmailLogsDialog({ open: false, logs: [] });
      }}>
        <DialogContent className="bg-[#111111] border-[#27272A] max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-500" />
              {t('emailLogs')}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {emailLogsDialog.logs.length} {t('emailsSent')}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] space-y-3">
            {emailLogsDialog.logs.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noEmailsSent')}</p>
            ) : (
              emailLogsDialog.logs.map((log, index) => (
                <div 
                  key={index}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-bold flex items-center gap-2">
                      {log.recipient_email}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        log.email_type === 'welcome' 
                          ? 'bg-[#007AFF]/20 text-[#007AFF]' 
                          : 'bg-[#FF9500]/20 text-[#FF9500]'
                      }`}>
                        {log.email_type === 'welcome' ? t('welcomeEmail') : t('resetEmail')}
                      </span>
                    </p>
                    <p className="text-sm text-gray-400">
                      {log.success ? (
                        <span className="text-green-400">✓ {t('sent')}</span>
                      ) : (
                        <span className="text-red-400">✗ {t('failed')}: {log.error}</span>
                      )}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.sent_at)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Club Members Dialog */}
      <Dialog open={clubMembersDialog.open} onOpenChange={(open) => {
        if (!open) setClubMembersDialog({ open: false, clubName: '', clubId: '', members: [] });
      }}>
        <DialogContent className="bg-[#111111] border-[#27272A] max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#34C759]" />
              {t('clubMembers')}: {clubMembersDialog.clubName}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {clubMembersDialog.members.length} {t('members')}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] space-y-3">
            {clubMembersDialog.members.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noMembersYet')}</p>
            ) : (
              clubMembersDialog.members.map((member) => (
                <div 
                  key={member.user_id}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-bold">{member.name}</p>
                    <p className="text-sm text-gray-400">{member.email}</p>
                    {member.nickname && (
                      <p className="text-xs text-gray-500">{t('nickname')}: {member.nickname}</p>
                    )}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline"
                        className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                      >
                        <UserX className="w-4 h-4 mr-2" />
                        {t('kickMember')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">{t('kickMemberTitle')}</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          {t('kickMemberWarning', { name: member.name, club: clubMembersDialog.clubName })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleKickMember(member.user_id, member.name)}
                          className="bg-[#FF3B30] hover:bg-[#D6301E]"
                        >
                          {t('kickMember')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;